/* File to hold lesson rubric constants
Text within Strapi's lesson.content will choose which rubric to display
*/

const Skills = {
  BusinessAcumen: 'business_acumen',
  BusinessStrategy: 'business_strategy',
  ClarifyingQuestions: 'clarifying_questions',
  Collaboration: 'collaboration',
  ComfortWithMetrics: 'comfort_with_metrics',
  ComfortWithNumbers: 'comfort_with_numbers',
  Communication: 'communication',
  CompetitiveAnalysis: 'competitve_analysis',
  Creativity: 'creativity',
  Curiosity: 'curiosity',
  DataLiteracy: 'data_literacy',
  Diagnosis: 'diagnosis',
  Execution: 'execution',
  LogicalThinking: 'logical_thinking',
  MarketAnalysis: 'market_analysis',
  PassionAndCreativity: 'passion_and_creativity',
  PossibleErrors: 'possible_errors',
  Prioritization: 'prioritization',
  ProblemIntuition: 'problem_intuition',
  ProblemSolving: 'problem_solving',
  ProductVision: 'product_vision',
  Roadmapping: 'roadmapping',
  SoftwareImplementation: 'software_implementation',
  TechnicalCommunication: 'technical_communication',
  TradeoffsAndErrors: 'tradeoffs_and_errors',
  UserCentricity: 'user_centricity',
  VariableIsolation: 'variable_isolation',
};

export const Scale = {
  option_1: 'Very weak',
  option_2: 'Weak',
  option_3: 'Neutral',
  option_4: 'Strong',
  option_5: 'Very strong',
};

export const signals = {
  [Skills.BusinessAcumen]: {
    name: 'Business Acumen',
    scale: {
      [Scale.option_1]: 'Failed to show an understanding of the context of the business.',
      [Scale.option_2]: 'Struggled to tie back to business goals or company mission.',
      [Scale.option_3]: 'Discussion around business goals was unclear or flawed.',
      [Scale.option_4]: 'Clearly discussed business goals, positioning, industry trends.',
      [Scale.option_5]: 'Nuanced understanding of the landscape, insightful arguments, logical assumptions.',
    },
  },
  [Skills.BusinessStrategy]: {
    name: 'Business Strategy',
    scale: {
      [Scale.option_1]: 'Failed to consider business goals and basic strategy.',
      [Scale.option_2]: 'Strategy unclear or full of errors.',
      [Scale.option_3]: 'Showed basic business sense, articulated a reasonable strategy with some guidance.',
      [Scale.option_4]: 'Delivered a logical strategy that fit greater business context.',
      [Scale.option_5]: `Strong strategic thinking and nuanced understanding of
        business landscape; compelling arguments.`,
    },
  },
  [Skills.ClarifyingQuestions]: {
    name: 'Clarifying Questions',
    scale: {
      [Scale.option_1]: 'Failed to ask questions and/or interact with the interviewer.',
      [Scale.option_2]: 'Struggled to ask the right questions and/or made assumptions without clarifying.',
      [Scale.option_3]: 'Asked good clarifying questions, but missed key points.',
      [Scale.option_4]: 'Asked insightful questions and adapted design to fit.',
      [Scale.option_5]: 'Asked surprising and insightful questions, came up with high-quality, novel design(s). ',
    },
  },
  [Skills.Collaboration]: {
    name: 'Collaboration',
    scale: {
      [Scale.option_1]: 'Failed to take the lead, did not respond to guidance.',
      [Scale.option_2]: 'Struggled to stay on track without guidance.',
      [Scale.option_3]: 'Took the lead and performed well; may have needed redirects or hints.',
      [Scale.option_4]: 'Effectively led the discussion, involved the interviewer throughout.',
      [Scale.option_5]: 'Took the lead and made exceptional use of the interviewer, discussion was more collaboration than interview.',
    },
  },
  [Skills.ComfortWithMetrics]: {
    name: 'Comfort with Metrics',
    scale: {
      [Scale.option_1]: 'Failed to show basic understanding of relevant metrics.',
      [Scale.option_2]: 'Struggled to define metrics for given problems and/or compare different metrics.',
      [Scale.option_3]: 'Showed reasonable knowledge, but missed "best fit" metrics for a given problem.',
      [Scale.option_4]: 'Clearly discussed pros and cons of various metrics, made a solid argument for choice(s).',
      [Scale.option_5]: 'Thoroughly and accurately discussed metrics, recommended ways to streamline analytical processes.',
    },
  },
  [Skills.ComfortWithNumbers]: {
    name: 'Comfort with Numbers',
    scale: {
      [Scale.option_1]: 'Failed to calculate on the fly or show competence with numerical relationships.',
      [Scale.option_2]: 'Attempts to quantify answer were sloppy or needed lots of guidance.',
      [Scale.option_3]: 'Arrived at a fair answer with a nudge or two.',
      [Scale.option_4]: 'Calculations were accurate and quick, approximations were helpful and appropriate.',
      [Scale.option_5]: `Showed above-average "ease" in using numbers to an advantage; creative use of approximation, "rules of thumb", etc. 
      Screen reader support enabled.`,
    },
  },
  [Skills.Communication]: {
    name: 'Communication',
    scale: {
      [Scale.option_1]: 'Failed to communicate clearly despite repeated prompts.',
      [Scale.option_2]: 'Poor communication throughout; interviewer had trouble following despite prompts.',
      [Scale.option_3]: 'Communication varied. Clear in some areas but vague or incomplete in others.',
      [Scale.option_4]: 'Good communication skills; articulated thought process clearly and consistently.',
      [Scale.option_5]: `Clear, proactive communication; anticipated questions, articulated reasons for
        decision, "checked-in" throughout.`,
    },
  },
  [Skills.CompetitiveAnalysis]: {
    name: 'Competitive Analysis',
    scale: {
      [Scale.option_1]: 'Failed to consider competition.',
      [Scale.option_2]: 'Poor understanding of competitive landscape.',
      [Scale.option_3]: 'Showed fair understanding of key competitors and trends.',
      [Scale.option_4]: 'Extensive competitive insight into the relevant players and their positioning.',
      [Scale.option_5]: `Deep attention to competitive landscape; discussed possibilities,
        competitive strategies, and opportunities logically and creatively. `,
    },
  },
  [Skills.Creativity]: {
    name: 'Creativity',
    scale: {
      [Scale.option_1]: 'Failed to show enthusiasm or creative thinking.',
      [Scale.option_2]: 'Solutions were bland, did not show interest in the problem.',
      [Scale.option_3]: 'Showed reasonable insight, but nothing exceptional.',
      [Scale.option_4]: 'Thought processes were both logical and creative.',
      [Scale.option_5]: `Exceptional logic, creativity and "out of the box" thinking on display;
        novel solutions found.`,
    },
  },
  [Skills.Curiosity]: {
    name: 'Curiosity',
    scale: {
      [Scale.option_1]: 'Failed to show interest and / or ask good questions.',
      [Scale.option_2]: 'Attempted to collect information, but failed to intuit the right areas to dig into.',
      [Scale.option_3]: 'Asked good questions or had good ideas, but missed key points.',
      [Scale.option_4]: 'Asked great questions, worked to understand the entire problem context.',
      [Scale.option_5]: `Gathered information effectively; asked insightful and / or
       surprising questions that got to the heart of the issue.`,
    },
  },
  [Skills.DataLiteracy]: {
    name: 'Data Literacy',
    scale: {
      [Scale.option_1]: 'Failed to use data to answer the question.',
      [Scale.option_2]: 'Failed to identify obvious patterns in data.',
      [Scale.option_3]: 'Showed reasonable ability to reason with data, but missed key points.',
      [Scale.option_4]: 'Used data effectively; asked good questions, made solid assumptions, formed logical conclusions.',
      [Scale.option_5]: `Extracted valuable insights from data; identified key patterns, suggested reports/tests to run,
        made logical arguments.`,
    },
  },
  [Skills.Diagnosis]: {
    name: 'Diagnosis',
    scale: {
      [Scale.option_1]: 'Failed to probe the origin of the problem; rushed into an answer.',
      [Scale.option_2]: 'Attempted to diagnose, but made serious errors or needed significant guidance.',
      [Scale.option_3]: 'Made a fair diagnosis, but missed nuances.',
      [Scale.option_4]: 'Asked good questions and used answers effectively to scope a solvable problem.',
      [Scale.option_5]: 'Asked insightful questions; set up a clear, easy-to-use test plan.',
    },
  },
  [Skills.Execution]: {
    name: 'Execution',
    scale: {
      [Scale.option_1]: 'Failed to define a clear plan to solve the problem.',
      [Scale.option_2]: 'Struggled to communicate action plan in an executable way.',
      [Scale.option_3]: `Plan was well-defined but missed key points, or the intuition
        was there but the plan wasn't fully executable.`,
      [Scale.option_4]: 'Correctly identified key elements of the problem and provided appropriate solutions, KPIs.',
      [Scale.option_5]: `Plan was clear and complete; Best-fit KPIs, bottlenecks/potential errors
        accounted for, and alternative solutions given.`,
    },
  },
  [Skills.LogicalThinking]: {
    name: 'Logical Thinking',
    scale: {
      [Scale.option_1]: 'Failed to probe the origin of the problem; rushed into an answer.',
      [Scale.option_2]: `Displayed some logical thought processes but made serious errors
        or needed significant guidance.`,
      [Scale.option_3]: 'Thought processes were somewhat logical despite a few errors.',
      [Scale.option_4]: 'Problem was effectively broken down and worked through in a logical way.',
      [Scale.option_5]: `Flawless logic; no mistakes or oversights, and logical thought
        was articulated clearly throughout.`,
    },
  },
  [Skills.MarketAnalysis]: {
    name: 'Market Analysis',
    scale: {
      [Scale.option_1]: 'Failed to consider the market.',
      [Scale.option_2]: 'Poor understanding of the market.',
      [Scale.option_3]: 'Fair uderstanding of key market forces, participants, and their interests.',
      [Scale.option_4]: 'Significant knowledge of relevant market dynamics.',
      [Scale.option_5]: 'Deep insight into the market; discussed opportunities logically and creatively.',
    },
  },
  [Skills.PassionAndCreativity]: {
    name: 'Passion and Creativity',
    scale: {
      [Scale.option_1]: 'Failed to show enthusiasm or creative thinking.',
      [Scale.option_2]: 'Solutions were bland; candidate did not show interest in the problem.',
      [Scale.option_3]: 'Displayed interest and reasonable insight but nothing exceptional.',
      [Scale.option_4]: 'Extensive knowledge, enthusiasm, and creativity on display throughout the interview.',
      [Scale.option_5]: 'Gave inspired answers; showed clear passion.',
    },
  },
  [Skills.PossibleErrors]: {
    name: 'Possible Errors',
    scale: {
      [Scale.option_1]: 'Failed to discuss possible errors.',
      [Scale.option_2]: 'Failed to reasonably justify decisions when pressed or made bad judgement calls.',
      [Scale.option_3]: 'Correctly identified some possible mistakes but missed others.',
      [Scale.option_4]: 'Correctly identified possible errors and suggested ways to increase accuracy with more information.',
      [Scale.option_5]: 'Possible errors covered thoroughly, alternatives were offered, pros and cons were neatly summarized.',
    },
  },
  [Skills.Prioritization]: {
    name: 'Prioritization',
    scale: {
      [Scale.option_1]: 'Failed to prioritize efforts when solving a problem.',
      [Scale.option_2]: 'Unclear or confusing prioritization',
      [Scale.option_3]: 'Prioritization was fair, given a nudge or two.',
      [Scale.option_4]: 'Prioritized effectively; accounted for urgency, cost/benefit, testability, etc.',
      [Scale.option_5]: 'Prioritized admirably; the proposed plan provided maximum benefit with minimal cost.',
    },
  },
  [Skills.ProblemIntuition]: {
    name: 'Problem Intuition',
    scale: {
      [Scale.option_1]: 'Failed to consider context; showed no intuition for where problems are likely to originate.',
      [Scale.option_2]: 'Intuition was incorrect and / or not justifiable when pressed.',
      [Scale.option_3]: 'Reasonable knowledge and intuition for where problems are likely to originate.',
      [Scale.option_4]: 'Strong sense of likely problem sources; able to test and confirm this intuition effectively.',
      [Scale.option_5]: 'Deep intuition for how the "system" works; plan & solution reflected this.',
    },
  },
  [Skills.ProblemSolving]: {
    name: 'Problem Solving',
    scale: {
      [Scale.option_1]: 'Failed to ask questions, rushed into an answer.',
      [Scale.option_2]: 'Serious errors or significant guidance needed in scoping the problem.',
      [Scale.option_3]: 'Scoped the problem, but missed key element(s).',
      [Scale.option_4]: 'Asked good questions and used answers effectively to scope a solvable problem.',
      [Scale.option_5]: 'Asked insightful questions; set up an easy calculation and an accurate answer.',
    },
  },
  [Skills.ProductVision]: {
    name: 'Product Vision',
    scale: {
      [Scale.option_1]: 'Failed to discuss the future of the product.',
      [Scale.option_2]: 'Struggled to articulate a vision for the future.',
      [Scale.option_3]: 'Laid out a possible future with some minor errors.',
      [Scale.option_4]: 'Displayed thoughtfulness and intuitition in articulating product vision.',
      [Scale.option_5]: `Exemplary product intuition; strong perspective, compelling
        arguments backed by data and strongly tied to UX.`,
    },
  },
  [Skills.Roadmapping]: {
    name: 'Roadmapping',
    scale: {
      [Scale.option_1]: 'Failed to articulate a product roadmap.',
      [Scale.option_2]: 'Roadmap unclear or full of errors.',
      [Scale.option_3]: 'Built a fair product roadmap with some guidance.',
      [Scale.option_4]: `Roadmap fit business, market / competitive landscape,
        assumptions realistic, alternatives given.`,
      [Scale.option_5]: 'Roadmap was clear, compelling, reasonable, and adaptive to changing circumstances.',
    },
  },
  [Skills.SoftwareImplementation]: {
    name: 'Software Implementation',
    scale: {
      [Scale.option_1]: 'Failed to show basic knowledge of how software is implemented.',
      [Scale.option_2]: 'Unable to dive deep into implementation processes.',
      [Scale.option_3]: 'Understands basic software implementation.',
      [Scale.option_4]: 'Solid understanding of technical processes underlying software products.',
      [Scale.option_5]: 'Deep, engineering-level understanding of software.',
    },
  },
  [Skills.TechnicalCommunication]: {
    name: 'Technical Communication',
    scale: {
      [Scale.option_1]: 'Failed to communicate with technical team members.',
      [Scale.option_2]: 'Struggled to translate "PM-speak" into technical terms; needed significant clarification.',
      [Scale.option_3]: 'Effective at translating requirements into technical terms.',
      [Scale.option_4]: `Accurately communicates requirements and requests in technical
        terms; can actively participate in a "live" technical discussion.`,
      [Scale.option_5]: 'Matches technical team in ability to communicate technically.',
    },
  },
  [Skills.TradeoffsAndErrors]: {
    name: 'Tradeoffs and Errors',
    scale: {
      [Scale.option_1]: 'Failed to mention tradeoffs and possible errors.',
      [Scale.option_2]: `Mentioned tradeoffs, but failed to justify decisions
        when pressed and/or made incorrect judgement calls.`,
      [Scale.option_3]: 'Covered possible errors and tradeoffs, but could have made better choices.',
      [Scale.option_4]: 'Logical tradeoff discussion, correctly identified possible errors.',
      [Scale.option_5]: `Deep knowledge and intuition around tradeoffs; alternatives
        offered, pros and cons neatly summarized. `,
    },
  },
  [Skills.UserCentricity]: {
    name: 'User-Centricity',
    scale: {
      [Scale.option_1]: 'Failed to consider the end user.',
      [Scale.option_2]: 'Struggled to anchor answer on end users despite guidance.',
      [Scale.option_3]: 'Attempted user-centric design, but missed key points.',
      [Scale.option_4]: 'Discussed pain points and opportunities and prioritized appropriately.',
      [Scale.option_5]: `Analyzed users accurately and completely, prioritized effectively,
        and tied back to users throughout.`,
    },
  },
  [Skills.VariableIsolation]: {
    name: 'Variable Isolation',
    scale: {
      [Scale.option_1]: 'Failed to isolate variables when problem-solving.',
      [Scale.option_2]: 'Variable isolation was sloppy or had obvious errors.',
      [Scale.option_3]: 'Isolated variables with reasonable skill, with a nudge or two.',
      [Scale.option_4]: `Isolated variables effectively; accounted for likelihood,
        urgency, cost/benefit, testability, etc.`,
      [Scale.option_5]: 'Isolated variables admirably; resulting action plan was elegant and robust.',
    },
  },
};

export const Rubrics = {
  'pm-estimation': {
    rubricName: 'pm-estimation',
    title: 'Product estimation skills',
    expert: {
      recommendation: 'Hire',
      text: 'Flavor text',
      scale: {
        [signals[Skills.ProblemSolving].name]: Scale.option_4,
        [signals[Skills.ComfortWithNumbers].name]: Scale.option_4,
        [signals[Skills.PossibleErrors].name]: Scale.option_4,
        [signals[Skills.Communication].name]: Scale.option_4,
        [signals[Skills.Collaboration].name]: Scale.option_4,
        [signals[Skills.Creativity].name]: Scale.option_4,
      },
    },
    exampleLessonUrl: 'https://www.tryexponent.com/courses/pm/estimation/estimation-rubric',
    skills: [
      signals[Skills.ProblemSolving],
      signals[Skills.ComfortWithNumbers],
      signals[Skills.PossibleErrors],
      signals[Skills.Communication],
      signals[Skills.Collaboration],
      signals[Skills.Creativity],
    ],
  },
  'pm-analytical': {
    rubricName: 'pm-analytical',
    title: 'Product analytical skills',
    expert: {
      recommendation: 'Hire',
      text: 'Flavor text',
      scale: {
        [signals[Skills.DataLiteracy].name]: Scale.option_4,
        [signals[Skills.ComfortWithMetrics].name]: Scale.option_4,
        [signals[Skills.Diagnosis].name]: Scale.option_4,
        [signals[Skills.Prioritization].name]: Scale.option_4,
        [signals[Skills.Execution].name]: Scale.option_4,
        [signals[Skills.Communication].name]: Scale.option_4,
        [signals[Skills.Collaboration].name]: Scale.option_4,
        [signals[Skills.Curiosity].name]: Scale.option_4,
      },
    },
    exampleLessonUrl: 'https://www.tryexponent.com/courses/pm/analytical/analytical-rubric',
    skills: [
      signals[Skills.DataLiteracy],
      signals[Skills.ComfortWithMetrics],
      signals[Skills.Diagnosis],
      signals[Skills.Prioritization],
      signals[Skills.Execution],
      signals[Skills.Communication],
      signals[Skills.Collaboration],
      signals[Skills.Curiosity],
    ],
  },
  'pm-execution': {
    rubricName: 'pm-execution',
    title: 'Product execution skills',
    expert: {
      recommendation: 'Hire',
      text: 'Flavor text',
      scale: {
        [signals[Skills.VariableIsolation].name]: Scale.option_4,
        [signals[Skills.DataLiteracy].name]: Scale.option_4,
        [signals[Skills.LogicalThinking].name]: Scale.option_4,
        [signals[Skills.ProblemIntuition].name]: Scale.option_4,
        [signals[Skills.Communication].name]: Scale.option_4,
        [signals[Skills.Collaboration].name]: Scale.option_4,
      },
    },
    exampleLessonUrl: 'https://www.tryexponent.com/courses/pm/execution/execution-rubric',
    skills: [
      signals[Skills.VariableIsolation],
      signals[Skills.DataLiteracy],
      signals[Skills.LogicalThinking],
      signals[Skills.ProblemIntuition],
      signals[Skills.Communication],
      signals[Skills.Collaboration],
    ],
  },
  'pm-product-design': {
    rubricName: 'pm-product-design',
    title: 'Product design skills',
    expert: {
      recommendation: 'Hire',
      text: 'Flavor text',
      scale: {
        [signals[Skills.BusinessAcumen].name]: Scale.option_4,
        [signals[Skills.UserCentricity].name]: Scale.option_4,
        [signals[Skills.ProductVision].name]: Scale.option_4,
        [signals[Skills.ClarifyingQuestions].name]: Scale.option_4,
        [signals[Skills.TradeoffsAndErrors].name]: Scale.option_4,
        [signals[Skills.PassionAndCreativity].name]: Scale.option_4,
        [signals[Skills.Communication].name]: Scale.option_4,
        [signals[Skills.Collaboration].name]: Scale.option_4,
      },
    },
    exampleLessonUrl: 'https://www.tryexponent.com/courses/pm/pm-product-design/product-design-rubric',
    skills: [
      signals[Skills.BusinessAcumen],
      signals[Skills.UserCentricity],
      signals[Skills.ProductVision],
      signals[Skills.ClarifyingQuestions],
      signals[Skills.TradeoffsAndErrors],
      signals[Skills.PassionAndCreativity],
      signals[Skills.Communication],
      signals[Skills.Collaboration],
    ],
  },
  'pm-product-strategy': {
    rubricName: 'pm-product-strategy',
    title: 'Product strategy skills',
    expert: {
      recommendation: 'Hire',
      text: 'Flavor text',
      scale: {
        [signals[Skills.CompetitiveAnalysis].name]: Scale.option_4,
        [signals[Skills.MarketAnalysis].name]: Scale.option_4,
        [signals[Skills.BusinessStrategy].name]: Scale.option_4,
        [signals[Skills.Roadmapping].name]: Scale.option_4,
        [signals[Skills.ClarifyingQuestions].name]: Scale.option_4,
        [signals[Skills.TradeoffsAndErrors].name]: Scale.option_4,
        [signals[Skills.PassionAndCreativity].name]: Scale.option_4,
        [signals[Skills.Communication].name]: Scale.option_4,
        [signals[Skills.Collaboration].name]: Scale.option_4,
      },
    },
    exampleLessonUrl: 'https://www.tryexponent.com/courses/pm/product-strategy/product-strategy-rubric',
    skills: [
      signals[Skills.CompetitiveAnalysis],
      signals[Skills.MarketAnalysis],
      signals[Skills.BusinessStrategy],
      signals[Skills.Roadmapping],
      signals[Skills.ClarifyingQuestions],
      signals[Skills.TradeoffsAndErrors],
      signals[Skills.PassionAndCreativity],
      signals[Skills.Communication],
      signals[Skills.Collaboration],
    ],
  },
  'pm-technical': {
    rubricName: 'pm-technical',
    title: 'Product technical skills',
    expert: {
      recommendation: 'Hire',
      text: 'Flavor text',
      scale: {
        [signals[Skills.DataLiteracy].name]: Scale.option_4,
        [signals[Skills.ComfortWithMetrics].name]: Scale.option_4,
        [signals[Skills.ClarifyingQuestions].name]: Scale.option_4,
        [signals[Skills.TradeoffsAndErrors].name]: Scale.option_4,
        [signals[Skills.SoftwareImplementation].name]: Scale.option_4,
        [signals[Skills.TechnicalCommunication].name]: Scale.option_4,
      },
    },
    exampleLessonUrl: 'https://www.tryexponent.com/courses/pm/pm-technical/technical-rubric',
    skills: [
      signals[Skills.DataLiteracy],
      signals[Skills.ComfortWithMetrics],
      signals[Skills.ClarifyingQuestions],
      signals[Skills.TradeoffsAndErrors],
      signals[Skills.SoftwareImplementation],
      signals[Skills.TechnicalCommunication],
    ],
  },
};
